import React from 'react';
import PropTypes from 'prop-types';
import lsUtil from '../../../components/common/lsUtil';
import MainModuleGroup from '../../../components/LayoutTemplateModularFullscreen/MainModuleGroup';
import SafetyScoreModuleGroupNavigation from './SafetyScoreModuleGroupNavigation';
import SafetyScoreModuleGroup from './SafetyScoreModuleGroup';

export const MINIMUM_KMS_FOR_SCORE = 1.60934; // 1 mile (scores are normalized to miles as well, so unitOfMeasure preference is not relevant or score criteria would be inconsistent between countries)
export const NOT_APPLICABLE_SCORE = -1; // used for entities with no trips
export const NOT_APPLICABLE_SCORE_PLACEHOLDER = "--"; // used in the UI (i.e. ScoreDetailsTable) for entities with NA scores and event scores

export const SUPPORTED_TIME_PERIOD_TYPES = {
    WEEKLY: "WEEKLY", // DEFAULT
    MONTHLY: "MONTHLY",
    QUARTERLY: "QUARTERLY",
};

export const REPORT_BY_TYPES = {
    DRIVER: "DRIVER", // DEFAULT if this.props.facialRecognitionSupported && this.props.facialRecognitionEnabled && this.props.knownIdentities.length > 0
    VEHICLE: "VEHICLE", // DEFAULT if !this.props.facialRecognitionSupported || !this.props.facialRecognitionEnabled || this.props.knownIdentities.length === 0
};

export const SUPPORTED_EVENTS_FACTORS_TYPES = {
    EVENT_AGGRESSIVE_ACCELERATION: "EVENT_AGGRESSIVE_ACCELERATION",
    EVENT_RAVEN_CAMERA_OBSCURED: "EVENT_RAVEN_CAMERA_OBSCURED",
    EVENT_RAVEN_CELLPHONE_DETECTED: "EVENT_RAVEN_CELLPHONE_DETECTED",
    EVENT_RAVEN_DISTRACTED_DETECTED: "EVENT_RAVEN_DISTRACTED_DETECTED",
    EVENT_RAVEN_DRINKING_DETECTED: "EVENT_RAVEN_DRINKING_DETECTED",
    EVENT_RAVEN_EATING_DETECTED: "EVENT_RAVEN_EATING_DETECTED",
    EVENT_RAVEN_TIRED_DETECTED: "EVENT_RAVEN_TIRED_DETECTED",
    EVENT_HARD_BRAKING: "EVENT_HARD_BRAKING",
    EVENT_HARD_CORNERING: "EVENT_HARD_CORNERING",
    EVENT_IDLING_STARTED: "EVENT_IDLING_STARTED",
    EVENT_POSSIBLE_IMPACT: "EVENT_POSSIBLE_IMPACT",
    EVENT_RAVEN_UNPLUGGED: "EVENT_RAVEN_UNPLUGGED",
    EVENT_RAVEN_SMOKING_DETECTED: "EVENT_RAVEN_SMOKING_DETECTED",
    EVENT_SPEEDING_STARTED: "EVENT_SPEEDING_STARTED",
    EVENT_RAVEN_VISION_TAILGATING: "EVENT_RAVEN_VISION_TAILGATING"
};

export const SUPPORTED_EVENTS_FACTORS_DEFAULT_WEIGHTS = {
    EVENT_AGGRESSIVE_ACCELERATION: 2,
    EVENT_RAVEN_CAMERA_OBSCURED: 3,
    EVENT_RAVEN_CELLPHONE_DETECTED: 5,
    EVENT_RAVEN_DISTRACTED_DETECTED: 4,
    EVENT_RAVEN_DRINKING_DETECTED: 2,
    EVENT_RAVEN_EATING_DETECTED: 2,
    EVENT_RAVEN_TIRED_DETECTED: 2,
    EVENT_HARD_BRAKING: 5,
    EVENT_HARD_CORNERING: 4,
    EVENT_IDLING_STARTED: 2,
    EVENT_POSSIBLE_IMPACT: 5,
    EVENT_RAVEN_UNPLUGGED: 3,
    EVENT_RAVEN_SMOKING_DETECTED: 5,
    EVENT_SPEEDING_STARTED: 3,
    EVENT_RAVEN_VISION_TAILGATING: 4
};

// Updated color palette for better stacked bar chart visibility and colorblind accessibility
export const EVENT_TYPE_COLORS = {
    EVENT_AGGRESSIVE_ACCELERATION: "#1f77b4", // Blue - A more distinct blue
    EVENT_RAVEN_CAMERA_OBSCURED: "#ff7f0e", // Orange - A more distinct orange
    EVENT_RAVEN_CELLPHONE_DETECTED: "#2ca02c", // Green - A more distinct green
    EVENT_RAVEN_DISTRACTED_DETECTED: "#9467bd", // Purple - A more distinct purple
    EVENT_RAVEN_DRINKING_DETECTED: "#8c564b", // Brown - A more distinct brown
    EVENT_RAVEN_EATING_DETECTED: "#e377c2", // Pink - A more distinct pink
    EVENT_RAVEN_TIRED_DETECTED: "#a55194", // Deep Pink/Magenta
    EVENT_HARD_BRAKING: "#d62728", // Red - A more distinct red
    EVENT_HARD_CORNERING: "#bcbd22", // Olive - A more distinct olive
    EVENT_IDLING_STARTED: "#17becf", // Cyan - A more distinct cyan
    EVENT_POSSIBLE_IMPACT: "#aec7e8", // Light Blue - A lighter shade of blue
    EVENT_RAVEN_UNPLUGGED: "#637939", // Darker Green
    EVENT_RAVEN_SMOKING_DETECTED: "#ffbb78", // Light Orange - A lighter shade of orange
    EVENT_SPEEDING_STARTED: "#98df8a", // Light Green - A lighter shade of green
    EVENT_RAVEN_VISION_TAILGATING: "#ff9896" // Light Red - A lighter shade of red
};

export const SUPPORTED_SCORE_CATEGORIES = {
    LOW: "LOW",
    MILD: "MILD",
    MEDIUM: "MEDIUM",
    HIGH: "HIGH"
};

export const SCORE_CATEGORY_DEFAULT_THRESHOLDS = {
    LOW: 90,
    MILD: 75,
    MEDIUM: 60,
    HIGH: 0 /* i.e. any score between 0 and 59 is high risk */
}

export const SCORE_CATEGORY_COLORS = {
    LOW: "#488F31",
    MILD: "#D0C755",
    MEDIUM: "#F39252",
    HIGH: "#CA3A7D"
};

export const colorForSafetyScoreEventType = (eventType) => {
    const color = EVENT_TYPE_COLORS[eventType];
    if (!color) {
        console.warn("SafetyScore colorForSafetyScoreEventType: unsupported event type: " + eventType);
        return "#000000";
    }
    return color;
};

export const colorForSafetyCategory = (scoreCategory) => {
    const color = SCORE_CATEGORY_COLORS[scoreCategory];
    if (!color) {
        console.warn("SafetyScore colorForSafetyCategory: unsupported score category: " + scoreCategory);
        return "#000000";
    }
    return color;
};

export const colorForSafetyScore = (score, selectedScoreThresholds) => {
    if (!selectedScoreThresholds) {
        console.warn("SafetyScore colorForSafetyScore: missing selectedScoreThresholds");
        return "#000000";
    }
    if (score === NOT_APPLICABLE_SCORE_PLACEHOLDER) {
        return "transparent"; // transparent as per CSS spec default for no color
    }
    if (score >= selectedScoreThresholds[SUPPORTED_SCORE_CATEGORIES.LOW]) {
        return SCORE_CATEGORY_COLORS[SUPPORTED_SCORE_CATEGORIES.LOW];
    }
    if (score >= selectedScoreThresholds[SUPPORTED_SCORE_CATEGORIES.MILD]) {
        return SCORE_CATEGORY_COLORS[SUPPORTED_SCORE_CATEGORIES.MILD];
    }
    if (score >= selectedScoreThresholds[SUPPORTED_SCORE_CATEGORIES.MEDIUM]) {
        return SCORE_CATEGORY_COLORS[SUPPORTED_SCORE_CATEGORIES.MEDIUM];
    }
    return SCORE_CATEGORY_COLORS[SUPPORTED_SCORE_CATEGORIES.HIGH];
};

export const localizedSortEventTypeIds = (availableEventsFactors) => {
    if (!availableEventsFactors || availableEventsFactors.length === 0) {
        return [];
    }
    const ids = availableEventsFactors.map(eventFactor => eventFactor.type);
    // sort by event name:
    ids.sort((eventTypeA, eventTypeB) => {
        const aName = document.getEventName({type: eventTypeA});
        const bName = document.getEventName({type: eventTypeB});
        return aName.localeCompare(bName);
    });

    return ids;
};


const convertKnownIdentitiesToDriverEntities = (knownIdentities) => {
    return knownIdentities.map(identity => ({
        id: identity.identityId,
        name: identity.name,
        selected: true
    }));
};

const convertRavensToVehicleEntities = (ravens) => {
    // skip ravens without vehicle names
    return ravens
        .filter(raven => raven.vehicle && raven.vehicle.name) // ignore ravens without vehicle names (not supported by reporting DB API)
        .map(raven => ({ // return formatted vehicle entity
            id: raven.ravenUuid,
            name: raven.vehicle.name,
            selected: true
        })
    );
};


// Local Storage Stand-in For Cloud-Based User Preferences

// localStorage keys
const SAFETY_SCORE_USER_PREFERENCES = "SAFETY_SCORE_USER_PREFERENCES"; // append user id to this key

// set user preferences in localStorage as a JSON object copy of the state
const setUserPreferences = (state, user) => {
    const userID = user.currentAccount ? user.currentAccount.accountId : "";
    const localStorageKey = SAFETY_SCORE_USER_PREFERENCES + userID;
    lsUtil.setObject(localStorageKey, state);
};

// get user preferences from localStorage
const getUserPreferences = (user) => {
    migrateUserPreferencesIfNeeded(user); // remove this once we are confident that all regular users have been migrated and the legacy localStorage key is no longer being used, as this is only needed to migrate old user preferences to the new user-specific localStorage key
    const userID = user.currentAccount ? user.currentAccount.accountId : "";
    const localStorageKey = SAFETY_SCORE_USER_PREFERENCES + userID;
    return lsUtil.getObject(localStorageKey);
};

const migrateUserPreferencesIfNeeded = (user) => {

    const userID = user.currentAccount ? user.currentAccount.accountId : "";

    const correctKey = SAFETY_SCORE_USER_PREFERENCES + userID;
    const legacyKey = SAFETY_SCORE_USER_PREFERENCES + "undefined";
    
    const correctData = lsUtil.getObject(correctKey);
    const legacyData = lsUtil.getObject(legacyKey);
    
    const correctKeyIsEmpty = !correctData || Object.keys(correctData).length === 0;
    const legacyKeyHasData = legacyData && Object.keys(legacyData).length > 0;
    
    // Only migrate if:
    // 1. Correct key has no data
    // 2. Legacy key has data
    // 3. We have a valid userID (not empty/undefined)
    if (userID && correctKeyIsEmpty && legacyKeyHasData) {
        lsUtil.setObject(correctKey, legacyData);
        lsUtil.removeItem(legacyKey);
    }
};

export default class SafetyScore extends React.Component {

    static propTypes = {
        refreshReportingTokenPromise: PropTypes.func.isRequired,
        user: PropTypes.object.isRequired,
        facialRecognitionSupported: PropTypes.bool,
        knownIdentities: PropTypes.array,
        ravens: PropTypes.array.isRequired,
        unitOfMeasure: PropTypes.string.isRequired,
        setMainModuleGroupNavigation: PropTypes.func.isRequired,
        onOptionsClick: PropTypes.func.isRequired
    };

    getDefaultEntititiesForReportBy = (reportBy) => {
        if (reportBy === REPORT_BY_TYPES.DRIVER) {
            return convertKnownIdentitiesToDriverEntities(this.props.knownIdentities);
        }
        if (reportBy === REPORT_BY_TYPES.VEHICLE) {
            return convertRavensToVehicleEntities(this.props.ravens);
        }
        console.warn("SafetyScore getDefaultEntititiesForReportBy: unsupported report by type: " + reportBy);
        return [];
    }

    getSystemDefaults = (props) => {

        const defaultReportBy = props.facialRecognitionSupported && props.facialRecognitionEnabled && props.knownIdentities && props.knownIdentities.length > 0 ? REPORT_BY_TYPES.DRIVER : REPORT_BY_TYPES.VEHICLE;

        return {
            selectedTimePeriodType: SUPPORTED_TIME_PERIOD_TYPES.WEEKLY, // default time period
            selectedReportBy: defaultReportBy,
            availableEntities: this.getDefaultEntititiesForReportBy(defaultReportBy),
            availableEventsFactors: Object.keys(SUPPORTED_EVENTS_FACTORS_TYPES).map(eventFactor => ({
                    type: SUPPORTED_EVENTS_FACTORS_TYPES[eventFactor],
                    weight: SUPPORTED_EVENTS_FACTORS_DEFAULT_WEIGHTS[eventFactor]
                })),
            selectedScoreThresholds: {...SCORE_CATEGORY_DEFAULT_THRESHOLDS}
        }
    };

    inititateUserDefaults = () => {
        return new Promise((resolve, reject) => { // using a promise b/c this will eventually be a cloud-based async load

            this.setState({ loadingUserPreferences: true });

            // load user preferences from localStorage
            const userPreferences = getUserPreferences(this.props.user);

            if (!userPreferences) {
                this.userSessionDefaultState = null;
                resolve(); // no user preferences, resolve the promise
                return;
            }

            if (!this.systemDefaultState) {
                console.warn("SafetyScore inititateUserDefaults: missing system default state");
                this.systemDefaultState = this.getSystemDefaults(this.props);
            }

            const reportByDriverIsSupported = this.props.facialRecognitionSupported && this.props.facialRecognitionEnabled && this.props.knownIdentities && this.props.knownIdentities.length > 0;

            if (userPreferences && userPreferences.selectedReportBy === REPORT_BY_TYPES.DRIVER && !reportByDriverIsSupported) {
                // if user preferences are set to driver report by, but the system or current Facial Rec settings do not support it, do not load incompatible user preferences
                this.userSessionDefaultState = null;
                resolve(); // no user preferences, resolve the promise
                return;
            }

            // User preferences override or augment system defaults, either partially or completely
            // That is, this is meant to have built-in forwards compatibility with future system defaults, even if the user preferences are behind recent upgrades
            this.userSessionDefaultState = {
                // since userPreferences might be empty or undefined, I'm using the ?. optional chaining operator
                selectedTimePeriodType: userPreferences?.selectedTimePeriodType || this.systemDefaultState.selectedTimePeriodType,
                selectedReportBy: userPreferences?.selectedReportBy || this.systemDefaultState.selectedReportBy,
                availableEntities: userPreferences?.availableEntities || [...this.systemDefaultState.availableEntities],
                availableEventsFactors: userPreferences?.availableEventsFactors || [...this.systemDefaultState.availableEventsFactors],
                selectedScoreThresholds: userPreferences?.selectedScoreThresholds || {...this.systemDefaultState.selectedScoreThresholds}
            }

            this.setState({
                loadingUserPreferences: false,
                ...this.userSessionDefaultState,
            }, () => {
                this.validateAllScoreFactors(() => { // validate the state
                    resolve(); // all state updates done, resolve the promise
                });
            });
        });
    };
    
    constructor(props) {

        super(props);

        // Relatively static states intended for comparison with this.state during edits:
        // If query configuration state diverges from this.systemDefaultState, show option to "Reset to Defaults"
        // If query configuration state diverges from this.userSessionDefaultState, show option to "Discard Changes"
        this.systemDefaultState = this.getSystemDefaults(props);
        this.userSessionDefaultState = null; // will be loaded from user preferences, if available, or will be null

        this.state = {

            loadingUserPreferences: true,
            stateDivergedFromDefaults: false,
            stateDivergedFromPreferences: false,
            stateDivergedFromAppliedQueryState: true, // true b/c there is no query state (i.e. no results in UI)

            allScoreFactorsAreValid: true, // true b/c starting with default values

            // query configuration state:
            ...this.systemDefaultState, // system defaults with the following properties:
                //selectedTimePeriodType
                //selectedReportBy
                //availableEntities // each entity has a "selected" property
                //availableEventsFactors // each event has a "weight" property, where 0 is equivalent to "not selected"
                //selectedScoreThresholds

            // applied query state (updated when user preferences are first loaded and onSearch() requests):
            appliedQueryState: null, // used to store the last applied query state
            appliedQueryStateUpdateId: null, // used to track the last applied query state update
            // If query configuration state diverges from this.state.appliedQueryState, enable "Apply" option (i.e. enable new search)
        };

    };

    resetAllStates = () => {
        // system defaults
        this.systemDefaultState = this.getSystemDefaults(this.props);
        // user session defaults
        this.inititateUserDefaults().then(() => { // updates this.userSessionDefaultState and component state
            // applied query state
            this.setState({
                appliedQueryState: null,
                appliedQueryStateUpdateId: null,
                stateDivergedFromAppliedQueryState: true, // true b/c there is no query state (assuming results were not loaded yet or were cleared in the UI)
            });
            this.checkAllForEventsFactorsChanges();
        }).catch((error) => {
            console.error("Error loading user defaults:", error);
        });
    };

    componentDidMount() {
        this.props.setMainModuleGroupNavigation(true); // hide the main module group navigation on first load

        this.resetAllStates();
    }

    componentDidUpdate(prevProps, prevState) {

        // Check if the props have changed and if so, update the state
        if (prevProps.facialRecognitionSupported !== this.props.facialRecognitionSupported ||
            prevProps.facialRecognitionEnabled !== this.props.facialRecognitionEnabled ||
            prevProps.knownIdentities !== this.props.knownIdentities ||
            //prevProps.ravens !== this.props.ravens || // TODO ravens updating (e.g. while driving) triggers unexpected re-rendering
            //prevProps.ravens?.length !== this.props.ravens?.length || // TODO I've observed the raven array length changing during a session!  I'm not sure what the cause is, but I can't use it for a shallow compare.
            prevProps.unitOfMeasure !== this.props.unitOfMeasure) {

            this.resetAllStates();
        }
    };

    statesAreDifferent = (stateA, stateB) => {
        return (
            stateA.selectedTimePeriodType !== stateB.selectedTimePeriodType ||
            stateA.selectedReportBy !== stateB.selectedReportBy ||
            stateA.availableEntities.some((entity, index) => entity.selected !== stateB.availableEntities[index].selected) ||
            stateA.availableEventsFactors.some((eventFactor, index) => eventFactor.weight !== stateB.availableEventsFactors[index].weight) ||
            Object.keys(stateA.selectedScoreThresholds).some((key) => {
                const defaultValue = stateB.selectedScoreThresholds[key];
                const selectedValue = stateA.selectedScoreThresholds[key];
                return (defaultValue !== selectedValue);
            })
        );
    };

    checkAllForEventsFactorsChanges = () => {
        const stateDivergedFromDefaults = this.statesAreDifferent(this.systemDefaultState, this.state);
        let stateDivergedFromPreferences = false;
        if (this.userSessionDefaultState) {
            stateDivergedFromPreferences = this.statesAreDifferent(this.userSessionDefaultState, this.state);
        }
        let stateDivergedFromAppliedQueryState = true; // assuming true (i.e. no results in UI) unless this.state.appliedQueryState is set
        if (this.state.appliedQueryState) {
            stateDivergedFromAppliedQueryState = this.statesAreDifferent(this.state.appliedQueryState, this.state);
        }

        this.setState({
            stateDivergedFromDefaults,
            stateDivergedFromPreferences,
            stateDivergedFromAppliedQueryState
        }, () => {
            if (stateDivergedFromDefaults || stateDivergedFromPreferences || stateDivergedFromAppliedQueryState) {
                this.validateAllScoreFactors();
            } else {
                this.setState({ allScoreFactorsAreValid: true });
            }
        });
    };

    validateAllScoreFactors = (optionalCallback) => {
        const { selectedTimePeriodType, selectedReportBy, availableEntities, availableEventsFactors, selectedScoreThresholds } = this.state;
        let allScoreFactorsAreValid = true;
        if (!selectedTimePeriodType) {
            allScoreFactorsAreValid = false;
        }
        if (!selectedReportBy) {
            allScoreFactorsAreValid = false;
        }
        if (!availableEntities || availableEntities.length === 0) {
            allScoreFactorsAreValid = false;
        } else {
            const firstSelectedEntity = availableEntities.find(entity => entity.selected);
            if (!firstSelectedEntity) {
                allScoreFactorsAreValid = false;
            }
        }
        if (!availableEventsFactors || availableEventsFactors.length === 0) {
            allScoreFactorsAreValid = false;
        } else {
            const firstNonZeroWeightFactor = availableEventsFactors.find(eventFactor => eventFactor.weight > 0);
            if (!firstNonZeroWeightFactor) {
                allScoreFactorsAreValid = false;
            }
        }
        if (!selectedScoreThresholds || Object.keys(selectedScoreThresholds).length === 0) {
            allScoreFactorsAreValid = false;
        }
        this.setState({ allScoreFactorsAreValid }, () => {
            if (optionalCallback && typeof optionalCallback === 'function') {
                optionalCallback();
            }
        });
    };

    onReportByChange = (reportBy) => {
        this.setState({
            selectedReportBy: reportBy,
            availableEntities: this.getDefaultEntititiesForReportBy(reportBy)
        }, this.checkAllForEventsFactorsChanges); // using callback to avoid delaying user state update
    }

    onTimePeriodChange = (timePeriod) => {
        this.setState({
            selectedTimePeriodType: timePeriod
        }, this.checkAllForEventsFactorsChanges);
    };

    onEntitiesChange = (entities) => {
        this.setState({
            availableEntities: [...entities]
        }, this.checkAllForEventsFactorsChanges);
    };

    onEventsFactorsChange = (eventsFactors) => {
        this.setState({
            availableEventsFactors: [...eventsFactors]
        }, this.checkAllForEventsFactorsChanges);
    };

    onScoreThresholdsChange = (scoreThresholds) => {
        this.setState({
            selectedScoreThresholds: scoreThresholds
        }, this.checkAllForEventsFactorsChanges);
    };

    onScoreFactorsSave = () => {
        // save user preferences to localStorage
        const pertinentState = {
            selectedTimePeriodType: this.state.selectedTimePeriodType,
            selectedReportBy: this.state.selectedReportBy,
            availableEntities: this.state.availableEntities,
            availableEventsFactors: this.state.availableEventsFactors,
            selectedScoreThresholds: this.state.selectedScoreThresholds
        };
        setUserPreferences(pertinentState, this.props.user);
        this.userSessionDefaultState = pertinentState;
        this.checkAllForEventsFactorsChanges();
    };

    onDiscardScoreFactorChanges = () => {
        // reset state with defaults
        let defaultState = this.userSessionDefaultState;
        if (!defaultState) {
            defaultState = this.systemDefaultState;
        }

        // reset state with system defaults
        this.setState({
            stateDivergedFromDefaults: this.statesAreDifferent(this.systemDefaultState, defaultState),
            stateDivergedFromPreferences: this.userSessionDefaultState ? this.statesAreDifferent(this.userSessionDefaultState, defaultState) : false,
            //allScoreFactorsAreValid: true,
            ...defaultState
        }, () =>{
            this.checkAllForEventsFactorsChanges();
            this.validateAllScoreFactors();
        }); // checking because it seems unsafe to assume the defaults are valid
    };

    onRestoreDefaults = () => {
        // reset state with system defaults
        this.setState({
            stateDivergedFromDefaults: this.statesAreDifferent(this.systemDefaultState, this.systemDefaultState),
            stateDivergedFromPreferences: this.userSessionDefaultState ? this.statesAreDifferent(this.userSessionDefaultState, this.systemDefaultState) : false,
            //allScoreFactorsAreValid: true,
            ...this.systemDefaultState
        }, () =>{
            this.checkAllForEventsFactorsChanges();
            this.validateAllScoreFactors();
        }); // checking because it seems unsafe to assume the defaults are valid
    };

    onSearch = () => {

        if (!this.state.allScoreFactorsAreValid) {
            console.warn("SafetyScore onSearch: all score factors are not valid");
            return;
        }
        
        // deep copy of the state
        const pertinentState = JSON.parse(JSON.stringify({
            selectedTimePeriodType: this.state.selectedTimePeriodType,
            selectedReportBy: this.state.selectedReportBy,
            availableEntities: this.state.availableEntities,
            availableEventsFactors: this.state.availableEventsFactors,
            selectedScoreThresholds: this.state.selectedScoreThresholds
        }));
        this.setState({
            appliedQueryState: pertinentState,
            appliedQueryStateUpdateId: Date.now(), // using timestamp as a simple update id
            stateDivergedFromAppliedQueryState: false,
        });
        this.props.setMainModuleGroupNavigation(true); // auto-hide the main module group navigation
    }

    render() {
        return (
            <MainModuleGroup

                mainModuleGroupNavigation = {
                    <SafetyScoreModuleGroupNavigation

                        facialRecognitionSupported = {this.props.facialRecognitionSupported}
                        facialRecognitionEnabled = {this.props.facialRecognitionEnabled}

                        selectedTimePeriodType = {this.state.selectedTimePeriodType}
                        onTimePeriodChange = {this.onTimePeriodChange}

                        selectedReportBy = {this.state.selectedReportBy}
                        onReportByChange = {this.onReportByChange}

                        availableEntities = {this.state.availableEntities}
                        onEntitiesChange = {this.onEntitiesChange}

                        availableEventsFactors = {this.state.availableEventsFactors}
                        onEventsFactorsChange = {this.onEventsFactorsChange}

                        selectedScoreThresholds = {this.state.selectedScoreThresholds}
                        onScoreThresholdsChange = {this.onScoreThresholdsChange}

                        stateDivergedFromDefaults = {this.state.stateDivergedFromDefaults}
                        stateDivergedFromPreferences = {this.state.stateDivergedFromPreferences}
                        stateDivergedFromAppliedQueryState = {this.state.stateDivergedFromAppliedQueryState}

                        allScoreFactorsAreValid = {this.state.allScoreFactorsAreValid}

                        onApply = {this.onSearch}
                        onSave = {this.onScoreFactorsSave}
                        onCancel = {this.onDiscardScoreFactorChanges}
                        onRestoreDefaults = {this.onRestoreDefaults}

                    />
                }

                mainModuleGroup = {
                    <SafetyScoreModuleGroup
                        onOptionsClick = {this.props.onOptionsClick}
                        onLoadScoreClick = {this.onSearch}
                        allScoreFactorsAreValid = {this.state.allScoreFactorsAreValid}
                        refreshReportingTokenPromise = {this.props.refreshReportingTokenPromise}
                        user = {this.props.user}
                        unitOfMeasure = {this.props.unitOfMeasure}
                        selectedTimePeriodType = {this.state.appliedQueryState?.selectedTimePeriodType}
                        selectedReportBy = {this.state.appliedQueryState?.selectedReportBy}
                        availableEntities = {this.state.appliedQueryState?.availableEntities}
                        availableEventsFactors = {this.state.appliedQueryState?.availableEventsFactors}
                        selectedScoreThresholds = {this.state.appliedQueryState?.selectedScoreThresholds}
                        appliedQueryStateUpdateId = {this.state.appliedQueryStateUpdateId}
                        stateDivergedFromDefaults = {this.state.stateDivergedFromDefaults}
                    />
                }

            />
        );
    }
};